import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Tabs} from 'antd';
import type {TabsProps} from 'antd';
import {LeftOutlined} from '@ant-design/icons';
import ServiceInfoStore from '@/store/ServiceInfoStore';
import {ServiceStatusEnum} from '@/dicts/enum';
import IconError from './assets/icon_error.svg';
import styles from './index.module.less';

interface ServiceStatusHeaderProps {
    serviceName?: string;
    tabsItems?: TabsProps['items'];
    activeKey?: TabsProps['activeKey'];
    onTabsChange?: TabsProps['onChange'];
}

const ServiceStatusHeader: React.FC<ServiceStatusHeaderProps> = ({
    serviceName = '服务管理',
    tabsItems = [],
    activeKey = '',
    onTabsChange = () => {},
}) => {
    const location = useLocation();
    const {pathname} = location;
    // 判断路由中包含/purchase-captcha
    const isPurchaseCaptcha = ['/purchase-captcha'].some(path => pathname.includes(path));
    if (isPurchaseCaptcha) {
        return (
            <header className={styles.headerContainer} style={tabsItems.length > 0 ? {paddingBottom: 0} : {}}>
                <div className={styles.titleRow}>
                    {/* 增加返回按钮，默认跳回上一页 */}
                    <Link to={-1} className={styles.backButton}>
                        <LeftOutlined style={{fontSize: 12, marginRight: 4}} />
                        返回
                    </Link>
                    <span className={styles.serviceName}>{serviceName}</span>
                </div>
            </header>
        );
    }

    const {
        serviceInfo,
    } = ServiceInfoStore;
    const {
        remainingCount,
        remainingDays,
        total,
        expiryDate,
        orderType,
        hasMoreCombo,
        status,
    } = serviceInfo;

    // 判断路由中是否包含/#/service-management
    const isServiceManagement = ['/service-management'].some(path => pathname.includes(path));

    // 判断是否是试用版状态
    const isTrialVersion = () => {
        return [ServiceStatusEnum.TRIAL_OPEN, ServiceStatusEnum.TRIAL_EXPIRED_OR_INSUFFICIENT].includes(status);
    };

    // 判断是否是后付费状态
    const isPostpayVersion = () => {
        return (status === ServiceStatusEnum.FORMAL_OPEN && orderType === 'postpay')
            || status === ServiceStatusEnum.FORMAL_POST_PAY_EXPIRED;
    };

    // 判断是否是预付费状态且需要提示的状态
    const isPrepayWarningStatus = () => {
        return status === ServiceStatusEnum.FORMAL_EXPIRED_OR_INSUFFICIENT
            || (status === ServiceStatusEnum.FORMAL_OPEN
            && orderType === 'prepay'
            && !hasMoreCombo
            && (remainingCount / total < 0.05 || remainingDays <= 7));
    };

    // 判断是否是正常预付费状态
    const isNormalPrepayVersion = () => {
        return status === ServiceStatusEnum.FORMAL_OPEN && orderType === 'prepay';
    };

    const showComboInfo = () => {
        if (!isServiceManagement) {
            return null;
        }

        if (isTrialVersion()) {
            return (
                <span className={styles.trialInfo}>
                    {`当前为试用版，套餐余量${remainingCount}次，剩余${remainingDays}天（${expiryDate}到期）；`}
                    <Link to="/purchase-captcha">立即购买</Link>
                </span>
            );
        }

        if (isPostpayVersion()) {
            return (
                <span className={styles.trialInfo}>
                    当前为后付费服务，按照 0.005元/次收费，建议购买次数包节省费用，
                    <Link to="/purchase-captcha">购买套餐</Link>
                </span>
            );
        }

        if (isPrepayWarningStatus()) {
            return (
                <span className={styles.trialInfo}>
                    {`套餐余量${remainingCount}次，剩余${remainingDays}天（${expiryDate}到期）；`}
                    余量不足或到期后，将按照 0.005元/次收费，
                    <Link to="/purchase-captcha">购买套餐</Link>
                </span>
            );
        }

        if (isNormalPrepayVersion()) {
            return (
                <span className={styles.trialInfo}>
                    {`套餐余量${remainingCount}次，剩余${remainingDays}天（${expiryDate}到期）`}
                </span>
            );
        }

        return null;
    };

    const showAlertInfo = () => {
        if (!isServiceManagement) {
            return null;
        }

        let alertText = '';
        if (status === ServiceStatusEnum.TRIAL_EXPIRED_OR_INSUFFICIENT) {
            // 试用版过期或次数用尽
            alertText = '试用版已到期，验证码服务暂停，购买验证码次数包后可继续使用';
        } else if (status === ServiceStatusEnum.FORMAL_POST_PAY_EXPIRED) {
            // 账户余额不足
            alertText = '账户余额不足，验证码服务暂停，购买次数包后可继续使用';
        }
        if (alertText.length === 0) {
            return null;
        }
        return (
            <div className={styles.alertBanner}>
                <img
                    src={IconError}
                    alt="warning"
                    className={styles.warningIcon}
                />
                <span className={styles.alertText}>
                    {alertText}
                </span>
            </div>
        );
    };

    return (
        <header className={styles.headerContainer} style={tabsItems.length > 0 ? {paddingBottom: 0} : {}}>
            <div className={styles.titleRow}>
                <span className={styles.serviceName}>{serviceName}</span>
                {showComboInfo()}
            </div>
            {/* 到期或套餐余量不足时显示警告 */}
            {showAlertInfo()}

            {tabsItems.length > 0 && (
                <Tabs
                    activeKey={activeKey}
                    items={tabsItems}
                    onChange={onTabsChange}
                />
            )}
        </header>
    );
};

export default ServiceStatusHeader;
